import { BaseController } from "./baseController";
import { type CICase, type CICaseDetail, type IncidentReport } from "components/CriticalIncident/interfaces";
import { type IncidentReportUpdatePayload } from "api/types";
import { isMockServerRunning } from "utils/isMockServerRunning";

export interface BaseIncidentReportParams {
    caseId: number;
    activityId: number;
    version?: string;
}

export interface CreateIncidentReportParams extends BaseIncidentReportParams {
    payload?: Partial<IncidentReport>;
}

export interface GetIncidentReportParams extends BaseIncidentReportParams {
    id: string;
}

export interface SubmitIncidentReportParams extends BaseIncidentReportParams {
    id: string;
    payload: IncidentReport;
}

export interface UpdateIncidentReportParams extends BaseIncidentReportParams {
    id?: string;
    payload: IncidentReportUpdatePayload;
}

export class IncidentReportController extends BaseController {
    private readonly isMockMode: boolean;

    static async resolveMockApiHost(): Promise<string> {
        return (await isMockServerRunning()) ? 'http://localhost:8009/api' : '/server/api';
    }

    constructor(apiHost: string, version = 'v1', isMockMode = false, isFeatureEnabled = true) {
        super(apiHost, version, isFeatureEnabled);
        this.isMockMode = isMockMode;
    }

    protected async resolveApiHost(version = this.version): Promise<string> {
        // TPH-733: there is no live v2 backend yet, so AU (v2) always routes to the mock server —
        // independent of the 'use-mock-server' session flag — until the real API is ready. Remove
        // the `version === 'v2'` branch once it is.
        return (this.isMockMode || version === 'v2') ? IncidentReportController.resolveMockApiHost() : this.apiHost;
    }

    async getIncidentReports({ caseId, activityId, version = this.version }: BaseIncidentReportParams): Promise<IncidentReport[]> {
        return await this.get<IncidentReport[], void>(await this.getEndpointAsync(`cases/${caseId}/activities/${activityId}/incident-reports`, version));
    }

    async getIncidentReport({ caseId, activityId, id, version = this.version }: GetIncidentReportParams): Promise<IncidentReport> {
        return this.get<IncidentReport, void>(await this.getEndpointAsync(`cases/${caseId}/activities/${activityId}/incident-reports/${id}`, version));
    }

    async createIncidentReport({caseId, activityId, payload, version = this.version}: CreateIncidentReportParams): Promise<string> {
        return this.post<string, Partial<IncidentReport>>(await this.getEndpointAsync(`cases/${caseId}/activities/${activityId}/incident-reports`, version), payload);
    }

    async updateIncidentReport({ caseId, activityId, id, payload, version = this.version }: UpdateIncidentReportParams): Promise<IncidentReport> {
        let response: string;
        if (!id) {
            response = await this.createIncidentReport({ caseId, activityId, version, payload: {} });
            id = response;
        }
        return this.patch<IncidentReport, IncidentReportUpdatePayload>(await this.getEndpointAsync(`cases/${caseId}/activities/${activityId}/incident-reports/${id}`, version), payload);
    }

    async submitIncidentReport({ caseId, activityId, id, payload, version = this.version }: SubmitIncidentReportParams): Promise<IncidentReport> {
        return this.post<IncidentReport, IncidentReport>(await this.getEndpointAsync(`cases/${caseId}/activities/${activityId}/incident-reports/${id}/submit`, version), payload);
    }

    async getCriticalIncidentCases(version = this.version): Promise<CICase[]> {
        return this.get<CICase[], void>(await this.getEndpointAsync(`cases?$type=critical_incident`, version));
    }

    async getCriticalIncidentCase({ caseId, activityId, version = this.version }: BaseIncidentReportParams): Promise<CICaseDetail> {
        return this.get<CICaseDetail, void>(await this.getEndpointAsync(`cases/${caseId}/activities/${activityId}?$type=critical_incident`, version));
    }
}
